import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { QrCode, CreditCard, Loader2, CheckCircle, Clock, Copy, ExternalLink, AlertCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { QRCodeCanvas } from 'qrcode.react';

export const PaymentModal = ({ 
  isOpen, 
  onClose, 
  plan,
  availablePlans = [],
  onPaymentSuccess 
}) => {
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pixError, setPixError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(plan);
  const [imgFailed, setImgFailed] = useState(false);

  const activePlan = selectedPlan || plan;
  
  // Form data
  const [formData, setFormData] = useState({
    payer_name: '',
    payer_email: '',
    payer_cpf: '',
    // Card fields
    card_number: '',
    card_expiry: '',
    card_cvv: '',
    card_holder: '',
    installments: 1
  });

  useEffect(() => {
    if (!isOpen) return;
    setPixData(null);
    setPixError(null);
    setPaymentStatus(null);
    setImgFailed(false);
    setSelectedPlan(plan);
    (async () => {
      const cfg = await loadPaymentConfig();
      if (cfg?.configured) {
        const payer = await loadPayerDefaults();
        // Gera o QR Code do PIX automaticamente se já temos os dados do cliente
        if (paymentMethod === 'pix' && payerComplete(payer)) {
          handlePixPayment(payer);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan]);

  useEffect(() => {
    // Poll para verificar status do PIX
    let interval;
    if (pixData && paymentStatus !== 'approved') {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/payment/status/${pixData.payment_id}`);
          if (response.data.status === 'approved') {
            setPaymentStatus('approved');
            toast.success('Pagamento aprovado!');
            if (onPaymentSuccess) onPaymentSuccess();
            clearInterval(interval);
          }
        } catch (error) {
          console.error('Erro ao verificar status:', error);
        }
      }, 5000); // Verificar a cada 5 segundos
    }
    return () => clearInterval(interval);
  }, [pixData, paymentStatus]);

  // Ao trocar de plano com um QR já gerado, regenera para o novo plano
  useEffect(() => {
    if (isOpen && pixData && paymentConfig?.configured && paymentMethod === 'pix') {
      const payer = {
        payer_name: formData.payer_name,
        payer_email: formData.payer_email,
        payer_cpf: formData.payer_cpf,
      };
      setPixData(null);
      setPaymentStatus(null);
      if (payerComplete(payer)) handlePixPayment(payer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id]);

  const loadPaymentConfig = async () => {
    setConfigLoading(true);
    setConfigError(false);
    try {
      const response = await api.get('/payment/config');
      setPaymentConfig(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar config de pagamento:', error);
      // Não confundir falha de requisição (403/rede/bundle antigo) com "não configurado"
      setConfigError(true);
      setPaymentConfig(null);
      return null;
    } finally {
      setConfigLoading(false);
    }
  };

  const onlyDigits = (v) => (v || '').replace(/\D/g, '');

  const formatDoc = (value) => {
    const n = onlyDigits(value);
    if (n.length > 11) {
      // CNPJ: 00.000.000/0000-00
      return n
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})/, '$1-$2')
        .slice(0, 18);
    }
    // CPF: 000.000.000-00
    return n
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .slice(0, 14);
  };

  const docIsValid = (doc) => {
    const n = onlyDigits(doc);
    return n.length === 11 || n.length === 14;
  };

  const payerComplete = (payer) =>
    !!(payer && payer.payer_name && payer.payer_email && docIsValid(payer.payer_cpf));

  const loadPayerDefaults = async () => {
    let user = {};
    try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { user = {}; }
    let company = {};
    try {
      const r = await api.get('/settings/company');
      company = r.data || {};
    } catch (e) { company = {}; }
    const doc = company.cnpj || company.cpf || '';
    const prefilled = {
      payer_name: company.razao_social || company.nome_fantasia || user.name || user.nome || '',
      payer_email: company.email || user.email || '',
      payer_cpf: doc ? formatDoc(doc) : '',
    };
    setFormData((prev) => ({ ...prev, ...prefilled }));
    return prefilled;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCPFChange = (e) => {
    const formatted = formatDoc(e.target.value);
    setFormData(prev => ({ ...prev, payer_cpf: formatted }));
  };

  const validateForm = () => {
    if (!formData.payer_name || !formData.payer_email || !formData.payer_cpf) {
      toast.error('Preencha todos os campos obrigatórios');
      return false;
    }
    if (!docIsValid(formData.payer_cpf)) {
      toast.error('CPF/CNPJ inválido');
      return false;
    }
    return true;
  };

  const getErrMsg = (error) => {
    const d = error?.response?.data?.detail;
    if (!d) return 'Não foi possível gerar o PIX. Tente novamente.';
    if (typeof d === 'string') return d;
    return d.mensagem || 'Não foi possível gerar o PIX. Tente novamente.';
  };

  const handlePixPayment = async (payerOverride) => {
    const payer = (payerOverride && payerOverride.payer_name) ? payerOverride : {
      payer_name: formData.payer_name,
      payer_email: formData.payer_email,
      payer_cpf: formData.payer_cpf,
    };
    // Auto-geração: se os dados não estão completos, não faz nada (mostra o formulário)
    if (payerOverride) {
      if (!payerComplete(payer)) return;
    } else if (!validateForm()) {
      return;
    }

    setLoading(true);
    setPixError(null);
    try {
      const response = await api.post('/payment/pix', {
        plan_id: activePlan.id,
        payer_name: payer.payer_name,
        payer_email: payer.payer_email,
        payer_cpf: onlyDigits(payer.payer_cpf)
      });
      
      if (response.data.success) {
        setPixData(response.data);
        setImgFailed(false);
        setPaymentStatus('pending');
        toast.success('QR Code PIX gerado com sucesso!');
      }
    } catch (error) {
      // Mostra o motivo REAL da falha (inclusive na auto-geração) para não travar a tela
      const msg = getErrMsg(error);
      setPixError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async () => {
    if (!validateForm()) return;
    
    // Validação adicional para cartão
    if (!formData.card_number || !formData.card_expiry || !formData.card_cvv || !formData.card_holder) {
      toast.error('Preencha todos os dados do cartão');
      return;
    }
    
    setLoading(true);
    try {
      // Aqui normalmente você usaria o SDK do Mercado Pago para tokenizar o cartão
      // Por segurança, nunca envie dados do cartão diretamente ao seu backend
      
      // Para demonstração, estamos enviando o token (em produção, use o SDK JS do MP)
      toast.warning('Em produção, use o SDK do Mercado Pago para tokenização segura do cartão');
      
      // Simular tokenização (em produção, usar SDK)
      const cardToken = 'demo_token_' + Date.now();
      
      const response = await api.post('/payment/card', {
        plan_id: activePlan.id,
        payer_name: formData.payer_name,
        payer_email: formData.payer_email,
        payer_cpf: formData.payer_cpf.replace(/\D/g, ''),
        card_token: cardToken,
        installments: formData.installments
      });
      
      if (response.data.success) {
        if (response.data.status === 'approved') {
          setPaymentStatus('approved');
          toast.success('Pagamento aprovado!');
          if (onPaymentSuccess) onPaymentSuccess();
        } else {
          toast.info(`Status: ${response.data.status_detail || response.data.status}`);
        }
      }
    } catch (error) {
      toast.error(getErrMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      toast.success('Código PIX copiado!');
    }
  };

  if (configLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="payment-loading-modal">
          <DialogHeader>
            <DialogTitle>Carregando pagamento...</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-sm text-slate-500">Verificando formas de pagamento disponíveis</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (configError) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="payment-error-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-500" />
              Não foi possível carregar o pagamento
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3">
            <p className="text-slate-700 text-sm">
              Houve uma falha ao verificar as formas de pagamento. Verifique sua conexão
              e tente novamente. Se o problema persistir, atualize a página (Ctrl+F5).
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadPaymentConfig} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="payment-retry-button">
              <Loader2 className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
            <Button onClick={onClose} variant="outline" className="flex-1">
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!paymentConfig?.configured) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="payment-unavailable-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Pagamento em breve disponível
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3">
            <p className="text-slate-700">
              Ainda estamos configurando o gateway de pagamento. Enquanto isso,
              entre em contato conosco para renovar/assinar seu plano manualmente:
            </p>
            <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm">
              <a
                href="https://wa.me/5511947289661?text=Ol%C3%A1%21%20Quero%20assinar%20o%20plano%20do%20nfcesysPdv"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
                data-testid="payment-whatsapp-link"
              >
                💬 WhatsApp: (11) 94728-9661
              </a>
              <a
                href="mailto:contato@nfcesys.com.br?subject=Quero%20assinar%20o%20plano"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                data-testid="payment-email-link"
              >
                ✉️ contato@nfcesys.com.br
              </a>
            </div>
            <p className="text-xs text-slate-500">
              Assim que confirmarmos o pagamento, seu plano é liberado no ato.
            </p>
          </div>
          <Button onClick={onClose} variant="outline" className="w-full">
            Fechar
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pagamento - {activePlan?.name}</DialogTitle>
          <DialogDescription>
            Valor: {formatCurrency(activePlan?.price || 0)}/mês
          </DialogDescription>
        </DialogHeader>

        {paymentStatus === 'approved' ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-emerald-600 mb-2">Pagamento Aprovado!</h3>
            <p className="text-slate-600">
              Seu plano {activePlan?.name} foi ativado com sucesso.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Você receberá uma confirmação por email e WhatsApp.
            </p>
            <Button onClick={onClose} className="mt-6">
              Fechar
            </Button>
          </div>
        ) : pixData ? (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="font-semibold mb-2">Escaneie o QR Code com seu banco</h3>
              
              {pixData.qr_code_base64 && !imgFailed ? (
                <div className="flex justify-center mb-4">
                  <img 
                    src={pixData.qr_code_base64.startsWith('data:') ? pixData.qr_code_base64 : `data:image/png;base64,${pixData.qr_code_base64}`} 
                    alt="QR Code PIX" 
                    className="w-64 h-64 border rounded-lg bg-white p-2"
                    data-testid="pix-qrcode-img"
                    onError={() => setImgFailed(true)}
                  />
                </div>
              ) : pixData.qr_code ? (
                <div className="flex justify-center mb-4">
                  <div className="border rounded-lg p-3 bg-white" data-testid="pix-qrcode">
                    <QRCodeCanvas value={pixData.qr_code} size={224} level="M" />
                  </div>
                </div>
              ) : (
                <div className="w-64 h-64 bg-slate-100 flex items-center justify-center mx-auto mb-4 rounded-lg">
                  <QrCode className="w-16 h-16 text-slate-400" />
                </div>
              )}
              
              <div className="flex items-center gap-2 justify-center mb-4">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-slate-600">Aguardando pagamento...</span>
              </div>
              
              {pixData.qr_code && (
                <div className="bg-slate-100 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Ou copie o código PIX:</p>
                  <div className="flex gap-2">
                    <Input 
                      value={pixData.qr_code} 
                      readOnly 
                      className="text-xs font-mono"
                    />
                    <Button onClick={copyPixCode} size="sm" variant="outline">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {pixData.ticket_url && (
                <a 
                  href={pixData.ticket_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mt-3"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir no Mercado Pago
                </a>
              )}
            </div>
            
            <Button onClick={() => setPixData(null)} variant="outline" className="w-full">
              Voltar
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {availablePlans.length > 1 && (
              <div className="border rounded-lg p-3 bg-slate-50" data-testid="payment-plan-selector">
                <p className="text-sm font-medium text-slate-700 mb-2">Escolha o plano para pagar:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {availablePlans.map((pl) => {
                    const isSel = pl.id === activePlan?.id;
                    const isAtual = pl.id === plan?.id;
                    return (
                      <button
                        key={pl.id}
                        type="button"
                        onClick={() => setSelectedPlan(pl)}
                        data-testid={`payment-plan-option-${pl.id}`}
                        className={`text-left border rounded-lg p-2 transition-all ${isSel ? 'border-emerald-500 ring-2 ring-emerald-200 bg-white' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm">{pl.name}</span>
                          {isAtual && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded">Atual</span>}
                        </div>
                        <span className="text-sm font-bold text-slate-800">{formatCurrency(pl.price)}<span className="text-xs font-normal text-slate-500">/mês</span></span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">Selecione o plano "Atual" para renovar, ou escolha outro para trocar.</p>
              </div>
            )}
          <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pix" className="flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                PIX
              </TabsTrigger>
              <TabsTrigger value="card" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Cartão
              </TabsTrigger>
            </TabsList>

            {/* Dados do Pagador */}
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome Completo *</Label>
                  <Input
                    name="payer_name"
                    value={formData.payer_name}
                    onChange={handleInputChange}
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    name="payer_email"
                    type="email"
                    value={formData.payer_email}
                    onChange={handleInputChange}
                    placeholder="seu@email.com"
                  />
                </div>
              </div>
              
              <div>
                <Label>CPF ou CNPJ *</Label>
                <Input
                  name="payer_cpf"
                  value={formData.payer_cpf}
                  onChange={handleCPFChange}
                  placeholder="CPF ou CNPJ"
                  maxLength={18}
                  data-testid="payer-document-input"
                />
              </div>
            </div>

            <TabsContent value="pix" className="mt-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <QrCode className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      Pague instantaneamente com PIX
                    </p>
                  </div>

                  {pixError && (
                    <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="pix-error-alert">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-rose-800">Não foi possível gerar o QR Code</p>
                          <p className="text-xs text-rose-700 mt-1">{pixError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    onClick={handlePixPayment} 
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="generate-pix-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Gerando QR Code...
                      </>
                    ) : (
                      <>
                        <QrCode className="w-4 h-4 mr-2" />
                        {pixError ? 'Tentar novamente' : `Gerar QR Code PIX - ${formatCurrency(activePlan?.price || 0)}`}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="card" className="mt-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Nota:</strong> Em produção, os dados do cartão são processados de forma segura pelo Mercado Pago.
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Número do Cartão</Label>
                  <Input
                    name="card_number"
                    value={formData.card_number}
                    onChange={handleInputChange}
                    placeholder="0000 0000 0000 0000"
                    maxLength={19}
                  />
                </div>
                
                <div>
                  <Label>Validade</Label>
                  <Input
                    name="card_expiry"
                    value={formData.card_expiry}
                    onChange={handleInputChange}
                    placeholder="MM/AA"
                    maxLength={5}
                  />
                </div>
                
                <div>
                  <Label>CVV</Label>
                  <Input
                    name="card_cvv"
                    value={formData.card_cvv}
                    onChange={handleInputChange}
                    placeholder="123"
                    maxLength={4}
                    type="password"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Nome no Cartão</Label>
                  <Input
                    name="card_holder"
                    value={formData.card_holder}
                    onChange={handleInputChange}
                    placeholder="NOME COMO NO CARTÃO"
                    className="uppercase"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Parcelas</Label>
                  <select
                    name="installments"
                    value={formData.installments}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value={1}>1x de {formatCurrency(activePlan?.price || 0)} (sem juros)</option>
                    <option value={2}>2x de {formatCurrency((activePlan?.price || 0) / 2)} (sem juros)</option>
                    <option value={3}>3x de {formatCurrency((activePlan?.price || 0) / 3)} (sem juros)</option>
                  </select>
                </div>
              </div>
              
              <Button 
                onClick={handleCardPayment} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pagar {formatCurrency(activePlan?.price || 0)}
                  </>
                )}
              </Button>
            </TabsContent>
          </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
